/**
 * 侧栏导航、工作台回首页、侧栏折叠、hash 路由。
 * 菜单信息架构见 docs/PE-AI-OS-系统整体改造规划.md 附录 A：
 * 分组即话术（投前/投中/投后与退出/智能底座/系统），p 编号仅为内部代号，UI 只显示业务名称。
 */

export const pages = {
  home: { title: '工作台', badge: '投资作业 AI 中台 · 投管退作业智能化' },
  p1: { title: '智能立项', badge: '上传 BP · 流式解析 · 可比案例' },
  p1r: { title: '智能立项 · BP 建档', badge: '草稿 · 待人工确认 · 可外部补齐' },
  p1s: { title: '智能立项 · 前置研判', badge: '六维证据评分 · AI 匹配依据' },
  p2: { title: '项目初评', badge: '综合评分 · 六维雷达 · 风险摘要' },
  p3: { title: '尽职调查', badge: '六维清单 · 资料需求 · 报告映射' },
  p4: { title: '估值建模', badge: '三表/DCF · Excel 导出 · 估值模型' },
  p5: { title: '投委会 · 材料生成', badge: '12章初稿 · 风险回报 · 审核提示' },
  p6: { title: '投委会 · 决策辅助', badge: '投决 Workflow · 决策摘要 · 审批' },
  p7: { title: '合同审阅', badge: '审阅 · 对比 · 一致性 · 补充协议' },
  p8: { title: '风险监控', badge: '32规则 · 四级监控 · 分级预警' },
  p8d: { title: '投后仪表盘', badge: '组合视图 · KPI 新鲜度 · 预警汇总' },
  p8v: { title: '价值创造', badge: '预算差异 · EBITDA桥 · 百日行动 · Excel' },
  p9: { title: '退出分析', badge: '路径对比 · 退出决策' },
  p10: { title: '预警中心', badge: '投后风控 · 四级分级预警' },
  p11: { title: '投资知识库', badge: 'Runtime 检索 · 有据回答' },
  p12: { title: 'Copilot 全局助手', badge: '项目上下文 · 有据回答' },
  p15: { title: '企业核验', badge: '工商核验 · KYC 初筛 · 有据引用' },
  p16: { title: '数据源管理', badge: 'Provider 状态 · 缓存 · capability' },
  p13: { title: '帮助中心 · 使用手册', badge: '总览 · 业务 · 技术 · 运维 · API' },
  p14: { title: '帮助中心 · 对接文档', badge: 'Integration Gateway v1 · REST' },
};

/** 页面 → hash 路由（附录 A.5）；p1r/p1s 作为 p1 子步骤仍保留深链接 */
const PAGE_HASH = {
  home: '#/home',
  p1: '#/pre/initiation',
  p1r: '#/pre/initiation/draft',
  p1s: '#/pre/initiation/prejudge',
  p2: '#/pre/review',
  p15: '#/pre/verify',
  p16: '#/sys/datasources',
  p3: '#/mid/diligence',
  p4: '#/mid/valuation',
  p7: '#/mid/contract',
  p5: '#/mid/committee',
  p6: '#/mid/committee/decision',
  p8: '#/post/risk',
  p8d: '#/post/portfolio',
  p8v: '#/post/value',
  p10: '#/post/alerts',
  p9: '#/post/exit',
  p11: '#/intel/knowledge',
  p12: '#/intel/copilot',
  p13: '#/sys/help',
  p14: '#/sys/help/api',
};
const HASH_PAGE = Object.fromEntries(
  Object.entries(PAGE_HASH).map(([page, hash]) => [hash, page]),
);

/** p1r/p1s 无侧栏独立入口，激活时高亮父级菜单 p1 */
const PARENT_NAV = { p1r: 'p1', p1s: 'p1' };

let currentHooks = {};
let currentPage = '';

function normalizeHash(raw) {
  const value = String(raw || '').trim();
  if (!value) return '';
  return value.split('?')[0].replace(/\/+$/, '');
}

/**
 * 统一的页面激活入口：侧栏点击、步骤条、深链接、程序化跳转全部走这里。
 * @param {string} pageId pages 表内 id
 * @param {{ silent?: boolean }} [opts] silent=true 时不同步 hash（hashchange 触发路径）
 */
export function activatePage(pageId, opts = {}) {
  const meta = pages[pageId];
  if (!meta) return false;
  currentPage = pageId;

  document.querySelectorAll('.mock-page').forEach((p) => p.classList.remove('active'));
  document.getElementById('page-' + pageId)?.classList.add('active');

  const navTarget = PARENT_NAV[pageId] || pageId;
  document.querySelectorAll('.nav-item').forEach((n) => {
    n.classList.toggle('active', n.dataset.page === navTarget);
  });

  document.getElementById('page-title').textContent = meta.title;
  document.getElementById('page-badge').textContent = meta.badge;

  // 页内步骤条（录入→BP建档→前置研判）同步高亮
  document.querySelectorAll('.flow-step[data-step-page]').forEach((step) => {
    step.classList.toggle('active', step.dataset.stepPage === pageId);
  });

  if (!opts.silent) {
    const hash = PAGE_HASH[pageId];
    if (hash && normalizeHash(window.location.hash) !== hash) {
      window.location.hash = hash; // 触发 hashchange，但因 pageId 已是 currentPage 而不会重入
    }
  }

  currentHooks.onPageEnter?.(pageId);
  return true;
}

/**
 * @param {{
 *   getCurrentProjectId?: () => string,
 *   onPageEnter?: (pageId: string) => void,
 * }} [hooks]
 */
export function initNav(hooks = {}) {
  currentHooks = hooks;

  document.querySelectorAll('.nav-item').forEach((item) => {
    if (item.classList.contains('nav-item-disabled')) return;
    if (item.dataset.externalHref) {
      item.addEventListener('click', function () {
        window.open(this.dataset.externalHref, '_blank', 'noopener,noreferrer');
      });
      return;
    }
    item.addEventListener('click', function () {
      activatePage(this.dataset.page || 'home');
    });
  });

  // 侧栏品牌标题：回工作台
  (function initBrandHomeLink() {
    const brand = document.getElementById('brand-home-link');
    if (!brand) return;
    function openHome() {
      activatePage('home');
    }
    brand.addEventListener('click', openHome);
    brand.addEventListener('keydown', function (event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        openHome();
      }
    });
  })();

  // 工作台 CTA / 卡片跳转
  document.querySelectorAll('[data-home-nav]').forEach((btn) => {
    btn.addEventListener('click', function () {
      const target = this.dataset.homeNav;
      if (target) activatePage(target);
    });
  });
  document.getElementById('home-open-flow')?.addEventListener('click', function () {
    const projectId = hooks.getCurrentProjectId?.() || '';
    activatePage(projectId ? 'p2' : 'p1');
  });

  // 页内步骤条（p1/p1r/p1s）
  document.querySelectorAll('.flow-step[data-step-page]').forEach((step) => {
    step.addEventListener('click', function () {
      activatePage(this.dataset.stepPage);
    });
  });

  // 侧栏折叠：低分屏下扩大内容区
  (function initSidebarToggle() {
    const sidebar = document.getElementById('app-sidebar');
    const toggle = document.getElementById('sidebar-toggle');
    if (!sidebar || !toggle) return;
    toggle.addEventListener('click', function () {
      const collapsed = sidebar.classList.toggle('collapsed');
      toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      toggle.textContent = collapsed ? '展开' : '菜单';
    });
  })();

  // hash 路由：浏览器前进/后退与深链接
  window.addEventListener('hashchange', function () {
    const pageId = HASH_PAGE[normalizeHash(window.location.hash)];
    if (pageId && pageId !== currentPage) {
      activatePage(pageId, { silent: true });
    }
  });

  // 初始路由：有 hash 走深链接，否则落工作台
  const initialPage = HASH_PAGE[normalizeHash(window.location.hash)];
  activatePage(initialPage || 'home', { silent: Boolean(!initialPage && !window.location.hash) });
}

/** 程序化导航（原侧栏点击模式已泛化，无侧栏入口的页面也可跳转） */
export function navigatePortalPage(pageId) {
  const page = String(pageId || '').trim();
  if (!page) return false;
  return activatePage(page);
}
